import React from 'react';
import Markdown from 'react-markdown';
import { useTranslation } from 'react-i18next';

import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import { mislecAPI } from '../../../../util/axios';
import Iconify from '../../../../theme/iconify/iconify';

const Feedback = ({ user, updateUser, courseId, sectionId, subsectionId }) => {
    const [feedback, setFeedback] = React.useState(null);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        setFeedback(user.courses.find(c => c.id === courseId)?.quiz.find(qz => qz.id === subsectionId)?.feedback);
        if (!user.courses.find(c => c.id === courseId)?.quiz.find(qz => qz.id === subsectionId)?.feedback) setFeedback(null);
    }, [user]);

    const { t } = useTranslation();

    const generateFeedback = () => {
        setLoading(true);

        const data = {
            course_id: courseId,
            section_id: sectionId,
            quiz_id: subsectionId
        };

        //  Feedback
        mislecAPI.post('/api/course/quiz/explain/full', data)
            .then(res => {
                updateUser({ ...user, courses: res.data.user_courses });
                setFeedback(res.data.feedback);
                setLoading(false);
            })
            .catch(error => {
                setLoading(false);

                //  Expired session
                if (error?.response)
                    if (error?.response?.data?.detail === 'api.auth.verify.401')
                        updateUser(null);
            })
    }

    return (
        <>
            {feedback && user.courses.find(c => c.id === courseId)?.quiz.find(qz => qz.id === subsectionId)?.questions.some(qx => qx.answers.some(a => a.id)) &&
                <Stack sx={{ backgroundColor: '#E6EEF9', borderRadius: '8px', p: 1.5 }}>
                    <Grid container alignItems='center'>
                        <Grid item md={1} xs={12} sx={{ display: 'flex', justifyContent: 'center', mb: 0.5, pr: 1 }}>
                            <Iconify icon='lucide:bot' width={32} sx={{ color: 'primary.main' }} />
                        </Grid>
                        <Grid item xs={11}>
                            <Grid container>
                                <Grid item xs={9} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant='h6' sx={{ fontSize: '0.9rem !important', color: 'primary.main' }}>
                                        {t('course.quiz.feedback')}
                                    </Typography>
                                </Grid>
                                <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Tooltip title={t('course.tooltips.regenerateFeedback')} arrow>
                                        <IconButton
                                            disabled={loading}
                                            onClick={() => {
                                                generateFeedback();
                                                setLoading(true);
                                            }}
                                            sx={{
                                                p: '4px',
                                                borderRadius: '8px',
                                                color: 'primary.main',
                                            }}
                                        >
                                            <Iconify icon='iconoir:repeat' width={20} />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                            {!loading &&
                                <Grid container sx={{ color: 'primary.main' }}>
                                    <Markdown className='mISLec_course_feedback'>
                                        {feedback}
                                    </Markdown>
                                </Grid>
                            }
                            {loading &&
                                <Grid container>
                                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'start', color: 'primary.main', mt: 0.5, mb: 1, pr: 1 }}>
                                        <Iconify
                                            icon='svg-spinners:3-dots-fade'
                                            style={{
                                                display: 'flex',
                                                alignSelf: 'flex-end',
                                                marginLeft: 1,
                                                marginBottom: 1
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            }
                        </Grid>
                    </Grid>
                </Stack>
            }
            {((!feedback && !loading) || !user.courses.find(c => c.id === courseId)?.quiz.find(qz => qz.id === subsectionId)?.questions.some(qx => qx.answers.some(a => a.id))) &&
                <Grid container sx={{ mt: '12px !important' }}>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mb: 0.5, pr: 1 }}>
                        <Tooltip title={!user.courses.find(c => c.id === courseId)?.quiz.find(qz => qz.id === subsectionId)?.questions.some(qx => qx.answers.some(a => a.id)) ? t('course.tooltips.answerForFeedback') : t('course.tooltips.feedback')} arrow>
                            <span style={{ display: 'inline-flex' }}>
                                <IconButton
                                    disabled={!user.courses.find(c => c.id === courseId)?.quiz.find(qz => qz.id === subsectionId)?.questions.some(qx => qx.answers.some(a => a.id))}
                                    onClick={() => generateFeedback()}
                                    sx={{
                                        p: '4px',
                                        borderRadius: '8px'
                                    }}
                                >
                                    <Iconify icon='mdi:feedback-outline' width={20} />
                                    <Typography variant='body2' sx={{ ml: 0.5, fontSize: '0.8rem', fontWeight: '700' }}>
                                        {t('course.quiz.feedback')}
                                    </Typography>
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Grid>
                </Grid>
            }
            {!feedback && loading &&
                <Grid container>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mt: 0.5, mb: 1, pr: 1 }}>
                        <CircularProgress size={20} />
                    </Grid>
                </Grid>
            }
        </>
    );
}

export default Feedback;